import dayjs from 'dayjs';
import {goalIsEnded} from '@utils/goal-status';
import {NotificationEvent, NotificationKind} from './notification.types';

type LegacyTask = Record<string, any>;

const taskKinds: Record<string, NotificationKind> = {
  checkin: 'checkin',
  comment: 'goalComment',
  mentions: 'goalMention',
  goals: 'goal',
  actionItem: 'actionItem',
  actionItems: 'actionItem',
  action_items: 'actionItem',
  feedbackRequests: 'feedback',
  payment: 'billing',
  upgradeAccount: 'billing',
  reviewPeers: 'performance',
  managerReviews: 'performance',
  peerReviews: 'performance',
  ccReviewNominatePeers: 'performance',
  ccReviewPeerNominations: 'performance',
  ccReviews: 'performance',
  selfReviews: 'performance',
};

const taskHrefs: Record<string, string> = {
  checkin: '/checkins/my-checkins',
  actionItem: '/action-items',
  actionItems: '/action-items',
  action_items: '/action-items',
  feedbackRequests: '/feedback/for-you',
  payment: '/workspace-settings/billing',
  upgradeAccount: '/workspace-settings/billing',
  ccReviews: '/culture-competence-review/your-reviews',
};

const getTimestamp = (value: LegacyTask) => {
  const candidate =
    value.createdAt || value.updatedAt || value.date || value.timestamp;
  if (!candidate) return undefined;
  const timestamp = new Date(candidate).valueOf();
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

const getTaskHref = (key: string, value: LegacyTask) => {
  if (taskHrefs[key]) return taskHrefs[key];
  if (key === 'comment' || key === 'mentions') {
    return value.id
      ? `/goals?goal=${encodeURIComponent(value.id)}&insight=true&comment=true`
      : '/goals';
  }
  if (key === 'goals') {
    return value.id
      ? `/goals?goal=${encodeURIComponent(value.id)}&insight=true`
      : '/goals';
  }
  if (key === 'managerReviews' && value.reviewCycleId) {
    return `/company/view-cycle/${value.reviewCycleId}`;
  }
  if (
    [
      'reviewPeers',
      'peerReviews',
      'ccReviewNominatePeers',
      'ccReviewPeerNominations',
    ].includes(key) &&
    value.reviewCycleId
  ) {
    return `/you/view-cycle/peer-review/${value.reviewCycleId}`;
  }
  return '/home';
};

export const cleanNotificationText = (
  text: string | undefined,
  userNames: Record<string, string> = {},
) =>
  (text || '')
    .replace(/_\(([^)]+)\)_/g, (_, userId) => userNames[userId] || 'A teammate')
    .replace(/<[^>]+>/g, '')
    .replace(/\\?"/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const stableTaskId = (key: string, value: LegacyTask, index: number) =>
  `task:${key}:${
    value.id || value.reviewCycleId || value.createdAt || index
  }:${cleanNotificationText(value.description || value.title).slice(0, 48)}`;

export const actionableGoalIds = (goals: any[]): Set<string> =>
  new Set(
    goals
      .filter((goal) => {
        const status = String(goal?.status || '').toLowerCase();
        return (
          goal?.id &&
          !goalIsEnded(goal) &&
          !['archived', 'deleted'].includes(status) &&
          !goal?.isArchived &&
          !goal?.isDeleted &&
          !goal?.deletedAt
        );
      })
      .map((goal) => String(goal.id)),
  );

export const normalizeTaskNotifications = (
  tasks: Record<string, any> | null | undefined,
  userNames: Record<string, string> = {},
  currentGoalIds: Set<string> = new Set(),
): NotificationEvent[] => {
  if (!tasks) return [];

  return Object.entries(tasks).flatMap(([key, rawValue]) => {
    // This legacy task represents the retired "review submitted check-ins"
    // workflow. It is intentionally excluded from the new inbox and counts.
    if (key === 'checkinFeedback') return [];

    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    return values.flatMap((value: LegacyTask, index) => {
      if (!value || typeof value !== 'object') return [];
      if (
        key === 'goals' &&
        (!value.id || !currentGoalIds.has(String(value.id)))
      ) {
        return [];
      }
      const title = cleanNotificationText(value.title, userNames);
      const body = cleanNotificationText(value.description, userNames);
      if (!title && !body) return [];

      return [
        {
          id: stableTaskId(key, value, index),
          kind: taskKinds[key] || 'general',
          source: 'taskList' as const,
          sourceId: value.id || value.reviewCycleId,
          title: title || 'Notification',
          body,
          href: getTaskHref(key, value),
          createdAt: getTimestamp(value),
          unread: true,
        },
      ];
    });
  });
};

export const normalizeCheckinCommentNotifications = (
  data: Record<string, any> | null | undefined,
): NotificationEvent[] => {
  if (!data) return [];

  return Object.entries(data).flatMap(([submissionId, value]) => {
    const unreadCount = Number(value?.unreadComment || 0);
    const mentionedBy = Array.isArray(value?.mentionedBy)
      ? value.mentionedBy.filter(Boolean)
      : [];
    const notification = value?.notification || {};
    const isMention = mentionedBy.length > 0;
    const isUnread = unreadCount > 0 || isMention;
    if (!isUnread && !notification.createdAt) return [];

    const actorName = notification.actorName || 'A teammate';
    const cycleName = notification.cycleName || 'a check-in response';
    const title = isMention
      ? `${actorName} mentioned you`
      : unreadCount === 1
      ? 'New comment on your check-in'
      : `${unreadCount || 1} new comments on your check-in`;
    const href =
      notification.href ||
      `/checkins/history?comments=${encodeURIComponent(submissionId)}`;

    return [
      {
        id: `checkin-comment:${submissionId}:${
          notification.createdAt || unreadCount || mentionedBy.join('-')
        }`,
        kind: isMention ? 'checkinMention' : 'checkinComment',
        source: 'checkinComments' as const,
        sourceId: submissionId,
        title,
        body: isMention
          ? `Open ${cycleName} to view the comment.`
          : `${actorName} commented on ${cycleName}.`,
        href,
        createdAt: notification.createdAt,
        actorName,
        unread: isUnread,
      },
    ];
  });
};

const goalActivityHref = (goalId: string, activity: 'comment' | 'tasks') =>
  `/goals?goal=${encodeURIComponent(goalId)}&insight=true&${
    activity === 'comment' ? 'comment' : 'action-items'
  }=true`;

const actorName = (actorIds: string[], userNames: Record<string, string>) => {
  const latestActor = actorIds[actorIds.length - 1];
  return (latestActor && userNames[latestActor]) || 'A teammate';
};

export const normalizeGoalActivityNotifications = (
  data: Record<string, any> | null | undefined,
  goalNames: Record<string, string> = {},
  userNames: Record<string, string> = {},
): NotificationEvent[] => {
  if (!data) return [];

  return Object.entries(data).flatMap(([goalId, value]) => {
    if (!value || typeof value !== 'object') return [];

    const notifications: NotificationEvent[] = [];
    const unreadComment = Number(value.unreadComment || 0);
    const mentionedBy = Array.isArray(value.mentionedBy)
      ? value.mentionedBy.filter(Boolean)
      : [];
    const unreadTasks = Number(value.unreadTasks || 0);
    const tasksMentionedBy = Array.isArray(value.tasksMentionedBy)
      ? value.tasksMentionedBy.filter(Boolean)
      : [];
    const goalName = goalNames[goalId] || 'a goal you follow';

    if (unreadComment > 0 || mentionedBy.length > 0) {
      const isMention = mentionedBy.length > 0;
      const name = actorName(mentionedBy, userNames);
      notifications.push({
        id: `goal-comment:${goalId}:${unreadComment}:${mentionedBy.join('-')}`,
        kind: isMention ? 'goalMention' : 'goalComment',
        source: 'goalActivity',
        sourceId: goalId,
        title: isMention
          ? `${name} mentioned you`
          : unreadComment === 1
          ? `New comment on ${goalName}`
          : `${unreadComment} new comments on ${goalName}`,
        body: isMention
          ? `${name} mentioned you on ${goalName}.`
          : `Open ${goalName} to view the conversation.`,
        href: goalActivityHref(goalId, 'comment'),
        createdAt: getTimestamp(value.notification || value),
        unread: true,
        actorName: isMention ? name : undefined,
      });
    }

    if (unreadTasks > 0 || tasksMentionedBy.length > 0) {
      const name = actorName(tasksMentionedBy, userNames);
      notifications.push({
        id: `goal-action-item:${goalId}:${unreadTasks}:${tasksMentionedBy.join(
          '-',
        )}`,
        kind: 'actionItemMention',
        source: 'goalActivity',
        sourceId: goalId,
        title: `${name} mentioned you in an action item`,
        body: `Open ${goalName} to view the action item.`,
        href: goalActivityHref(goalId, 'tasks'),
        createdAt: getTimestamp(value.taskNotification || value),
        unread: true,
        actorName: name,
      });
    }

    return notifications;
  });
};

const actionItemHref = (item: Record<string, any>) =>
  item.source === 'goal' && item.sourceId
    ? goalActivityHref(String(item.sourceId), 'tasks')
    : '/action-items';

export const normalizeActionItemNotifications = (
  data: Record<string, any> | null | undefined,
  userId: string,
  userNames: Record<string, string> = {},
  now = Date.now(),
): NotificationEvent[] => {
  if (!data || !userId) return [];

  return Object.entries(data).flatMap(([firebaseId, item]) => {
    if (!item || typeof item !== 'object' || item.completed) return [];

    const assigneeId =
      typeof item.assignee === 'string' ? item.assignee : item.assignee?.id;
    if (assigneeId !== userId) return [];

    const itemId = String(item.id || item._id || item.key || firebaseId);
    const itemText = cleanNotificationText(item.text) || 'Untitled action item';
    const href = actionItemHref(item);
    const notifications: NotificationEvent[] = [];
    const creatorId =
      typeof item.createdBy === 'string' ? item.createdBy : item.createdBy?.id;
    const createdAt = getTimestamp({createdAt: item.createdAt});

    if (creatorId && creatorId !== userId) {
      const name = userNames[creatorId] || 'A teammate';
      notifications.push({
        id: `action-item-assigned:${itemId}:${createdAt || 'existing'}`,
        kind: 'actionItem',
        source: 'actionItems',
        sourceId: itemId,
        title: 'Action item assigned to you',
        body: `${name} assigned “${itemText}” to you.`,
        href,
        createdAt,
        unread: true,
        actorName: name,
      });
    }

    const dueDate = dayjs(item.dueDate);
    if (dueDate.isValid() && dayjs(now).isAfter(dueDate, 'day')) {
      notifications.push({
        id: `action-item-overdue:${itemId}:${dueDate.format('YYYY-MM-DD')}`,
        kind: 'actionItemOverdue',
        source: 'actionItems',
        sourceId: itemId,
        title: 'Action item overdue',
        body: `“${itemText}” was due ${dueDate.format('MMM D')}.`,
        href,
        createdAt: dueDate.add(1, 'day').startOf('day').valueOf(),
        unread: true,
      });
    }

    return notifications;
  });
};

export const sortNotifications = (notifications: NotificationEvent[]) =>
  [...notifications].sort((left, right) => {
    if (left.unread !== right.unread) return left.unread ? -1 : 1;
    return (right.createdAt || 0) - (left.createdAt || 0);
  });

export type NotificationGroupLabel = 'Today' | 'Yesterday' | 'Earlier';

export const groupNotifications = (notifications: NotificationEvent[]) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.valueOf() - 24 * 60 * 60 * 1000);
  const groups: Record<NotificationGroupLabel, NotificationEvent[]> = {
    Today: [],
    Yesterday: [],
    Earlier: [],
  };

  notifications.forEach((notification) => {
    if (!notification.createdAt) {
      groups.Earlier.push(notification);
      return;
    }
    const created = new Date(notification.createdAt);
    if (created >= today) groups.Today.push(notification);
    else if (created >= yesterday) groups.Yesterday.push(notification);
    else groups.Earlier.push(notification);
  });

  return groups;
};

export const formatNotificationTime = (createdAt?: number) => {
  if (!createdAt) return '';
  const elapsed = Math.max(Date.now() - createdAt, 0);
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return dayjs(createdAt).format('MMM D, h:mm A');
};
